// src/components/sessions/ReportFormModal.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function ReportFormModal({
  isOpen,
  onClose,
  onSubmit,
  students = [],
  isSubmitting = false
}) {
  const { t } = useTranslation();
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [reportContent, setReportContent] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedStudentId(students.length === 1 ? students[0].institution_member_id : '');
      setReportContent('');
      setError('');
    }
  }, [isOpen, students]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!selectedStudentId) {
      setError(t('reports.form.errors.selectStudent'));
      return;
    }
    if (!reportContent.trim()) {
      setError(t('reports.form.errors.contentRequired'));
      return;
    }
    if (reportContent.length > 5000) {
      setError(t('reports.form.errors.contentTooLong'));
      return;
    }

    onSubmit({
      studentInstitutionMemberId: parseInt(selectedStudentId, 10),
      reportContent: reportContent.trim(),
      homework: null,
      rating: null
    });
  };

  return (
    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-30" onClick={!isSubmitting ? onClose : undefined} />
      <div className="relative bg-white rounded-xl shadow-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <button
          onClick={!isSubmitting ? onClose : undefined}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <h3 className="text-lg font-semibold text-slate-800 mb-1">
          {t('reports.form.titleCreate')}
        </h3>
        <p className="text-xs text-slate-500 mb-4">{t('reports.form.immutableNotice')}</p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Student select */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('reports.form.student')}
              </label>
              {students.length === 1 ? (
                <div className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-700">
                  {students[0].display_name}
                </div>
              ) : (
                <select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  disabled={isSubmitting}
                >
                  <option value="">{t('reports.form.selectStudent')}</option>
                  {students.map(s => (
                    <option key={s.institution_member_id} value={s.institution_member_id}>
                      {s.display_name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Report content */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('reports.form.content')}
              </label>
              <textarea
                value={reportContent}
                onChange={(e) => setReportContent(e.target.value)}
                rows={5}
                maxLength={5000}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-vertical"
                placeholder={t('reports.form.contentPlaceholder')}
                disabled={isSubmitting}
              />
              <p className="text-xs text-slate-400 mt-1">{reportContent.length}/5000</p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={!isSubmitting ? onClose : undefined}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('reports.form.saving') : t('reports.form.createReport')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
